import { ACTIVITY_EVENTS } from 'Constants/constants'
import { Subscription } from './Subscription'
import debounce from 'lodash/debounce'

export enum TaskActivityEventType {
    UserActive = 'userActive',
    UserInactive = 'userInactive',
    InactivityThreshold = 'inactivityThreshold',
}

export type TaskActivityMonitorConfig = {
    isEnabled: boolean
    minimumDurationMinutes: number
    responseTimeoutSeconds: number
}

type TaskActivityEventData = {
    [TaskActivityEventType.UserActive]: void
    [TaskActivityEventType.UserInactive]: void
    [TaskActivityEventType.InactivityThreshold]: void
}

export class TaskActivityMonitor {
    private config: TaskActivityMonitorConfig
    private subscription: Subscription<TaskActivityEventType, TaskActivityEventData>
    private inactivityTimer?: number
    private responseTimer?: number
    private isMonitoring: boolean = false
    private isPaused: boolean = false

    constructor(config: TaskActivityMonitorConfig) {
        this.config = config
        this.subscription = new Subscription<TaskActivityEventType, TaskActivityEventData>()
    }

    getConfig() {
        return this.config
    }

    startMonitoring(): void {
        if (!this.config.isEnabled || this.isMonitoring) {
            return
        }

        this.isMonitoring = true
        this.addEventListeners()
        this.startInactivityTimer()
    }

    stopMonitoring(): void {
        this.isMonitoring = false
        this.removeEventListeners()
        this.clearTimers()
    }

    pauseMonitoring(): void {
        this.isPaused = true
        this.clearTimers()
    }

    resumeMonitoring(): void {
        this.isPaused = false
        this.clearTimers()
        this.startInactivityTimer()
    }

    on<T extends TaskActivityEventType>(
        event: T,
        callback: (data: TaskActivityEventData[T]) => void
    ): string {
        return this.subscription.subscribe(event, callback)
    }

    off(eventId: string): void {
        this.subscription.unsubscribe(eventId)
    }

    private addEventListeners(): void {
        ACTIVITY_EVENTS.forEach(event => {
            document.addEventListener(event, this.handleActivity)
        })
    }

    private removeEventListeners(): void {
        ACTIVITY_EVENTS.forEach(event => {
            document.removeEventListener(event, this.handleActivity)
        })
    }

    private debouncedActivityHandler = debounce(() => {
        this.clearTimers()
        this.startInactivityTimer()
    }, 500)

    private handleActivity = (): void => {
        if (!this.isMonitoring || this.isPaused) {
            return
        }
        this.debouncedActivityHandler()
    }

    private startInactivityTimer(): void {
        if (!this.isMonitoring || this.isPaused) {
            return
        }

        const timeoutMs = this.config.minimumDurationMinutes * 60 * 1000

        this.inactivityTimer = window.setTimeout(
            this.handleInactivityThreshold.bind(this),
            timeoutMs
        )
    }

    private handleInactivityThreshold(): void {
        if (!this.isMonitoring || this.isPaused) {
            return
        }

        this.subscription.publishUpdate(TaskActivityEventType.InactivityThreshold)

        this.startResponseTimer()
    }

    private startResponseTimer(): void {
        this.responseTimer = window.setTimeout(
            this.handleNoResponse.bind(this),
            this.config.responseTimeoutSeconds * 1000
        )
    }

    private handleNoResponse(): void {
        this.subscription.publishUpdate(TaskActivityEventType.UserInactive)
    }

    private clearTimers(): void {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer)
            this.inactivityTimer = undefined
        }
        if (this.responseTimer) {
            clearTimeout(this.responseTimer)
            this.responseTimer = undefined
        }
        this.debouncedActivityHandler.cancel()
    }
}
